import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { ArrowLeft, Save, X } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import {
  Admin,
  AdminUpdatePayload,
} from '../../../common/models/general-settings'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import {
  getGeneralSettingsAdminById,
  updateAdmin,
} from '../../../core/services/general-settings'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

interface IAdmins {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  lastActivity: string
  active: boolean
  password?: string | null
}

export const AdminSettingsPage: React.FC = () => {
  const [admin, setAdmin] = useState<Admin>()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const navigate = useNavigate()
  const { adminId } = useParams()

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        if (adminId) {
          const resp = await getGeneralSettingsAdminById(adminId)
          if (resp.status === 200) {
            setAdmin(resp.data)
            setValue('id', resp.data.id)
            setValue('firstName', resp.data.firstName)
            setValue('lastName', resp.data.lastName)
            setValue('email', resp.data.email)
            setValue('phone', resp.data.phone)
            setValue('lastActivity', formatDateTime(resp.data.lastActivityDate))
            setValue('active', resp.data.active)
            setValue('password', '')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const schema = yup.object().shape({
    id: yup.string().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().required(),
    phone: yup.string().required(),
    lastActivity: yup.string().required(),
    active: yup.boolean().required(),
    password: yup
      .string()
      .notRequired()
      .min(6)
      .nullable()
      .transform((value) => (!!value ? value : null)),
  })

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm<IAdmins>({
    mode: 'onChange',
    resolver: yupResolver<IAdmins>(schema),
    defaultValues: {
      password: null,
    },
  })

  const submit = async (values: IAdmins) => {
    setSubmitLoading(true)
    try {
      setLoading(true)
      if (admin) {
        const data: AdminUpdatePayload = {
          phone: values.phone,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          active: values.active,
        }
        if (values.password) {
          data.password = values.password
        }
        const response = await updateAdmin(admin.id, data)
        if (response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
      setSubmitLoading(false)
    }
  }

  const handleCancel = async () => {
    if (admin) {
      setValue('id', admin.id)
      setValue('firstName', admin.firstName)
      setValue('lastName', admin.lastName)
      setValue('phone', admin.phone)
      setValue('email', admin.email)
      setValue('active', admin.active)
      setValue('password', '')
    }
  }

  return (
    <>
      <Header
        pageTitle="Admin Settings"
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(submit)}
              disabled={loading || submitLoading}
              loading={submitLoading}
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        }
      />
      {loading && <Loader />}
      <div className="flex flex-row items-center justify-between gap-3 mb-5 w-full">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <Icon
            icon="arrow-left"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
          Back
        </Button>
        <span className="text-title-s text-monochrome-1">
          {admin ? `${admin.firstName} ${admin.lastName}` : 'Add New Admin'}
        </span>
        <div className="min-w-[82px]" />
      </div>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 max-h-[calc(100vh-178px)] overflow-y-auto space-y-4">
        <form
          id="settings-email-form"
          onSubmit={handleSubmit(submit)}
          className="flex flex-col gap-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="First Name" error={errors?.firstName?.message}>
              <Input
                {...register('firstName')}
                type="text"
                placeholder="Enter Name"
                className={errors?.firstName?.message ? 'border-accent-3' : ''}
              />
            </FormGroup>
            <FormGroup label="Last Name" error={errors?.lastName?.message}>
              <Input
                {...register('lastName')}
                type="text"
                placeholder="Enter Name"
                className={errors?.lastName?.message ? 'border-accent-3' : ''}
              />
            </FormGroup>
            <FormGroup label="Phone Number" error={errors?.phone?.message}>
              <Input
                {...register('phone')}
                type="tel"
                placeholder="Enter Phone Number"
                className={errors?.phone?.message ? 'border-accent-3' : ''}
              />
            </FormGroup>
            <FormGroup label="Email" error={errors?.email?.message}>
              <Input
                {...register('email')}
                type="email"
                placeholder="Enter Email"
                className={errors?.email?.message ? 'border-accent-3' : ''}
              />
            </FormGroup>
            <FormGroup label="Password" error={errors?.password?.message}>
              <Input
                {...register('password')}
                type="password"
                placeholder="Enter Password"
                className={errors?.password?.message ? 'border-accent-3' : ''}
              />
            </FormGroup>
          </div>
          <FormGroup
            label="Last Activity:"
            direction="row"
            labelClassName="whitespace-nowrap !text-monochrome-2 text-body-medium"
          >
            <div className="text-monochrome-1 text-body-medium">
              {admin?.lastActivityDate
                ? formatDateTime(admin.lastActivityDate)
                : '-'}
            </div>
          </FormGroup>
          <FormGroup
            label="Active"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            direction="row"
            labelProps={{
              htmlFor: 'active',
            }}
          >
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="active"
                  ref={field.ref}
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            form="settings-email-form"
            type="submit"
            disabled={loading || submitLoading}
            loading={submitLoading}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
